import type { ComponentChildren } from "preact";
import { createPortal } from "preact";
import { useEffect, useId, useRef } from "preact/hooks";
import { cx } from "../cx";
import { useEscape } from "../hooks/hooks";
import { useArrowNav } from "../hooks/useArrowNav";

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: ComponentChildren;
  /** Buttons row at the bottom. */
  footer?: ComponentChildren;
  size?: "sm" | "md" | "lg";
  /** Escape and a click on the backdrop close the dialog. Default true. */
  dismissible?: boolean;
  class?: string;
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Dialog rendered into document.body. Traps Tab, closes on Escape (through the app Escape stack,
 * so it wins over "back") and restores focus to the previously focused element on close. Put
 * `data-autofocus` on the control that should take focus first.
 */
export function Modal({ title, onClose, children, footer, size = "md", dismissible = true, class: cls }: ModalProps) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);

  useEscape(() => {
    if (dismissible) onClose();
  });
  useArrowNav(dialog);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = dialog.current;
    if (root) {
      const first = root.querySelector<HTMLElement>("[data-autofocus]") ?? root.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? root).focus();
    }
    return () => previous?.focus();
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const root = dialog.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      e.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === root)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      class="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault(); // a click beside the dialog must not move focus out of it
        if (dismissible) onClose();
      }}
    >
      <div
        ref={dialog}
        class={cx("modal", `modal--${size}`, cls)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header class="modal__head">
          <h2 id={titleId} class="modal__title">
            {title}
          </h2>
          {dismissible && (
            <button type="button" class="modal__close" aria-label="Close" onClick={onClose}>
              <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                <path d="M2 2 14 14M14 2 2 14" stroke="currentColor" stroke-width="2" fill="none" />
              </svg>
            </button>
          )}
        </header>
        <div class="modal__body">{children}</div>
        {footer && <footer class="modal__foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
